import { createContext, type ReactNode, useContext, useMemo } from "react";

import type { V2PermissionProps, V2ResourceToPermission } from "./types-v2";
import type { ResourcePermissionInput } from "./use-resource-permission";

/**
 * A runtime gate that force-denies access to descendants of
 * `PermissionLockProvider`. When a descendant `Permissioned*` component's v2
 * permission structurally matches any entry in `permissions`, the descendant
 * is treated as unauthorized and `tooltip` is shown.
 *
 * Use this for cross-cutting locks (e.g., live-sync edit lock) so we don't
 * have to thread lock state through every Permissioned* call site.
 *
 * Note: this only force-denies. It cannot grant access that the underlying
 * permission lookup denies — that would be a security footgun.
 */
export type PermissionLock = {
  permissions: V2PermissionProps<V2ResourceToPermission>[];
  tooltip: string;
};

const PermissionLockContext = createContext<PermissionLock | null>(null);

type ProviderProps = {
  lock: PermissionLock | null;
  children: ReactNode;
};

export const PermissionLockProvider = ({ lock, children }: ProviderProps) => {
  const value = useMemo(() => lock, [lock]);
  return (
    <PermissionLockContext.Provider value={value}>
      {children}
    </PermissionLockContext.Provider>
  );
};

export const usePermissionLock = (
  permission?: ResourcePermissionInput<V2ResourceToPermission>,
): { isLocked: boolean; tooltip: string | undefined } => {
  const lock = useContext(PermissionLockContext);
  if (!lock || !permission) {
    return { isLocked: false, tooltip: undefined };
  }
  const matched = lock.permissions.some((lockPermission) =>
    v2PermissionMatches(permission.v2, lockPermission),
  );
  return matched
    ? { isLocked: true, tooltip: lock.tooltip }
    : { isLocked: false, tooltip: undefined };
};

function v2PermissionMatches(
  a: V2PermissionProps<V2ResourceToPermission>,
  b: V2PermissionProps<V2ResourceToPermission>,
): boolean {
  if (a.resource !== b.resource || a.grant !== b.grant) return false;
  const aId = "id" in a && a.id !== undefined ? String(a.id) : undefined;
  const bId = "id" in b && b.id !== undefined ? String(b.id) : undefined;
  return aId === bId;
}
